/* Saved floors – the save hearts on product cards ([data-fs-heart]), the product photo heart (same attribute) and the
   header "Saved floors" heart ([data-fs-saved-link]). Loaded once from the header (snippets/fs-saved.liquid).

   Saving needs a customer account (owner, 4 Oct 2026). The list lives on the account: customer metafield
   custom.saved_floors (list.product_reference), shown on the account "Saved floors" page (app/extensions/fs-saved-floors).

   Guests: a heart opens the "Sign in to save floors" prompt (#FsSavePrompt). The tapped floor waits in sessionStorage
   ("fs-save-after-login") and is saved as soon as they come back signed in.

   Signed in: the account list arrives in the page (#FsSavedConfig, rendered from the metafield), so hearts are right on
   load with no request. A tap flips the heart at once. The theme can't write customer metafields itself (new customer
   accounts + no hosted backend), so changes not yet on the account wait in localStorage "fs-saved-pending"
   ({add, remove}) and are written by the Saved floors page: every link to it (toast, header heart) carries
   ?add=…&remove=…&return=… . Once the account list contains them they drop out of the queue on the next page.
   Old device-only saves ("fs-saved", from before sign-in was required) move into the queue once, then are deleted. */
(() => {
  if (window.FSSaved) return;
  const d = document;
  const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const MAX = 50;
  const PENDING = 'fs-saved-pending';
  const AFTER_LOGIN = 'fs-save-after-login';
  const LEGACY = ['fs-saved', 'fs-saved-removed'];

  let cfg = {};
  try {
    cfg = JSON.parse(d.getElementById('FsSavedConfig')?.textContent || '{}');
  } catch (e) {}
  const signedIn = cfg.signedIn === true;
  const account = (cfg.account || []).filter((h) => HANDLE_RE.test(h));
  const clean = (list) => [...new Set((Array.isArray(list) ? list : []).filter((h) => HANDLE_RE.test(h)))];

  const store = (area) => {
    try {
      return window[area];
    } catch (e) {
      return null;
    }
  };
  const local = store('localStorage');
  const session = store('sessionStorage');
  const readJSON = (s, key, fallback) => {
    try {
      return JSON.parse(s?.getItem(key) || 'null') ?? fallback;
    } catch (e) {
      return fallback;
    }
  };

  /* ---------------------------------------------------------------- state */
  let pending = { add: [], remove: [] };
  let notice = null; // one message to show once the page is ready (after-login save / moved device saves)

  if (signedIn) {
    const p = readJSON(local, PENDING, {});
    pending = {
      add: clean(p.add).filter((h) => !account.includes(h)),
      remove: clean(p.remove).filter((h) => account.includes(h)),
    };
    // one-off: saves kept on this device before sign-in was required
    const legacy = clean(readJSON(local, 'fs-saved', [])).filter((h) => !account.includes(h) && !pending.add.includes(h));
    if (legacy.length) {
      pending.add = [...pending.add, ...legacy];
      notice = { text: `We’ve kept the ${legacy.length === 1 ? 'floor' : `${legacy.length} floors`} you saved on this device`, link: 'Add to my account' };
    }
    LEGACY.forEach((k) => {
      try {
        local?.removeItem(k);
      } catch (e) {}
    });
    // the floor a guest tapped before signing in
    const after = readJSON(session, AFTER_LOGIN, null);
    if (after && HANDLE_RE.test(after.handle)) {
      pending.remove = pending.remove.filter((h) => h !== after.handle);
      if (!account.includes(after.handle) && !pending.add.includes(after.handle)) pending.add.unshift(after.handle);
      notice = { text: `${after.name || 'Floor'} saved`, link: account.includes(after.handle) ? '' : 'Add to my account' };
    }
    try {
      session?.removeItem(AFTER_LOGIN);
    } catch (e) {}
    pending.add = pending.add.slice(0, MAX);
    persist();
  }

  function persist() {
    try {
      if (pending.add.length || pending.remove.length) local?.setItem(PENDING, JSON.stringify(pending));
      else local?.removeItem(PENDING);
      return true;
    } catch (e) {
      return false;
    }
  }

  const list = () => (signedIn ? [...pending.add, ...account.filter((h) => !pending.remove.includes(h))].slice(0, MAX) : []);
  const has = (handle) => list().includes(handle);
  const dirty = () => pending.add.length > 0 || pending.remove.length > 0;

  /** The Saved floors account page, carrying any changes it still has to write. */
  function accountLink() {
    if (!cfg.accountUrl) return '';
    if (!dirty()) return cfg.accountUrl;
    const url = new URL(cfg.accountUrl);
    if (pending.add.length) url.searchParams.set('add', pending.add.join(','));
    if (pending.remove.length) url.searchParams.set('remove', pending.remove.join(','));
    url.searchParams.set('return', location.href.split('#')[0]);
    return url.toString();
  }

  /* ---------------------------------------------------------------- hearts + header */
  const nameOf = (btn) => {
    if (!btn.dataset.fsName) btn.dataset.fsName = (btn.getAttribute('aria-label') || '').replace(/^Save\s+/, '') || 'this floor';
    return btn.dataset.fsName;
  };

  // Speed (12 Oct 2026): attributes are only written when they change – rewriting them on every pass invalidated style
  // for every card on the page.
  const attr = (el, k, v) => { v = String(v); if (el.getAttribute(k) !== v) el.setAttribute(k, v); };

  function paint() {
    const saved = list();
    d.querySelectorAll('[data-fs-heart]').forEach((btn) => {
      const on = saved.includes(btn.dataset.fsHeart);
      const name = nameOf(btn);
      attr(btn, 'aria-pressed', on);
      attr(btn, 'aria-label', on ? `Remove ${name} from saved floors` : `Save ${name}`);
      if (!signedIn) attr(btn, 'aria-haspopup', 'dialog');
    });
    d.querySelectorAll('[data-fs-saved-link]').forEach((a) => {
      if (signedIn && cfg.accountUrl) { const href = accountLink(); if (a.href !== href) a.href = href; }
      attr(a, 'aria-label', saved.length ? `Saved floors (${saved.length})` : 'Saved floors');
      const n = a.querySelector('[data-fs-saved-count]');
      if (n) {
        if (n.textContent !== String(saved.length)) n.textContent = saved.length;
        if (n.hidden !== !saved.length) n.hidden = !saved.length;
      }
    });
  }

  function toggle(btn) {
    const handle = btn.dataset.fsHeart;
    if (!HANDLE_RE.test(handle || '')) return;
    const name = nameOf(btn);
    if (!signedIn) return openPrompt(btn, { handle, name });
    const wasOn = has(handle);
    pending.add = pending.add.filter((h) => h !== handle);
    pending.remove = pending.remove.filter((h) => h !== handle);
    if (wasOn && account.includes(handle)) pending.remove.unshift(handle);
    if (!wasOn && !account.includes(handle)) pending.add = [handle, ...pending.add].slice(0, MAX);
    if (!persist()) return toast({ text: 'Sorry, saving isn’t available in this browser.' });
    paint();
    d.dispatchEvent(new CustomEvent('fs:saved', { detail: { handles: list() } }));
    toast({
      text: wasOn ? `${name} removed` : `${name} saved`,
      link: dirty() ? (pending.add.length ? 'Add to my account' : 'Update my account') : '',
    });
  }

  /* ---------------------------------------------------------------- toast */
  let toastEl;
  function toast({ text, link }) {
    if (!toastEl) {
      toastEl = d.createElement('div');
      toastEl.className = 'fs-saved-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      // keep it while the pointer or focus is on it (the link must be reachable)
      ['pointerenter', 'focusin'].forEach((ev) => toastEl.addEventListener(ev, () => clearTimeout(toastEl._t)));
      ['pointerleave', 'focusout'].forEach((ev) => toastEl.addEventListener(ev, () => hideLater(2500)));
      d.body.append(toastEl);
    }
    toastEl.replaceChildren(d.createTextNode(text));
    const href = link && accountLink();
    if (href) {
      const a = d.createElement('a');
      a.href = href;
      a.textContent = link;
      toastEl.append(' – ', a);
    }
    toastEl.classList.add('is-on');
    hideLater(href ? 7000 : 3000);
  }
  function hideLater(ms) {
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => toastEl.classList.remove('is-on'), ms);
  }

  /* ---------------------------------------------------------------- sign-in prompt (guests) */
  let opener = null;
  function openPrompt(from, item) {
    wirePrompt(); // a tap can come before the idle start-up below
    const dlg = d.getElementById('FsSavePrompt');
    // return to this very page after signing in (relative path only – that's all return_to accepts)
    const back = location.pathname + location.search;
    const login = new URL(cfg.loginUrl || '/customer_authentication/login', location.origin);
    login.searchParams.set('return_to', back);
    const href = login.toString();
    try {
      if (item) session?.setItem(AFTER_LOGIN, JSON.stringify(item));
      else session?.removeItem(AFTER_LOGIN);
    } catch (e) {}
    if (!dlg || typeof dlg.showModal !== 'function') return void (location.href = href);
    const go = dlg.querySelector('[data-fs-login]');
    go.href = href;
    dlg.querySelector('[data-fs-prompt-title]').textContent = item ? `Sign in to save ${item.name}` : 'Sign in to see your saved floors';
    opener = from;
    dlg.showModal();
    go.focus();
  }
  function wirePrompt() {
    const dlg = d.getElementById('FsSavePrompt');
    if (!dlg || dlg._wired) return;
    dlg._wired = true;
    const forget = () => {
      try {
        session?.removeItem(AFTER_LOGIN);
      } catch (e) {}
    };
    dlg.querySelectorAll('[data-fs-prompt-close]').forEach((b) => b.addEventListener('click', () => (forget(), dlg.close())));
    dlg.addEventListener('cancel', forget); // Esc
    dlg.addEventListener('click', (e) => e.target === dlg && (forget(), dlg.close())); // backdrop
    dlg.addEventListener('close', () => {
      opener?.focus?.();
      opener = null;
    });
  }

  /* ---------------------------------------------------------------- events */
  d.addEventListener('click', (e) => {
    const heart = e.target.closest?.('[data-fs-heart]');
    if (heart) {
      e.preventDefault(); // never follow a card link underneath
      return toggle(heart);
    }
    const link = !signedIn && e.target.closest?.('[data-fs-saved-link]');
    if (link && d.getElementById('FsSavePrompt')) {
      e.preventDefault();
      openPrompt(link, null);
    }
  });

  // Cards added later (Load more, filters, recommendations) get painted too – one pass per frame at most.
  // Speed (12 Oct 2026): only the page content is watched (all hearts live in <main>), only added nodes that carry a
  // heart count, and the first paint + the watcher start when the browser is idle after the page is parsed (the hearts' server
  // markup is already right for guests; the click handler above works from the start).
  const HEART = '[data-fs-heart]';
  const hasHeart = (n) => n.nodeType === 1 && (n.matches(HEART) || n.querySelector(HEART));
  let queued = false;
  const watch = () => {
    new MutationObserver((records) => {
      if (queued || !records.some((r) => [...r.addedNodes].some(hasHeart))) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint();
      });
    }).observe(d.getElementById('MainContent') || d.body, { childList: true, subtree: true });
  };

  const ready = () => {
    wirePrompt();
    paint();
    watch();
    if (notice) toast(notice);
  };
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(ready, { timeout: 2000 }) : setTimeout(ready, 1));
  d.readyState === 'loading' ? d.addEventListener('DOMContentLoaded', idle) : idle();

  window.FSSaved = { list, has, signedIn, accountLink };
})();
